<template>
  <div class="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mt-6">
    <h3 class="text-base font-bold text-black mb-3">Liste des références à valider</h3>
    
    <div v-if="references.length === 0" class="text-center py-6">
      <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <i class="fas fa-box text-black text-2xl"></i>
      </div>
      <p class="text-black">Aucun produit n'a été ajouté pour votre demande de mise en ligne.</p>
    </div>

    <div v-else>
      <!-- Résumé -->
      <div class="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 class="text-xs font-bold text-blue-800 mb-1">
          Liste des références à valider ({{ references.length }} référence{{ references.length > 1 ? 's' : '' }})
        </h4>
        <p class="text-xs text-black italic">
          Toutes les références ajoutées via {{ source === 'form' ? 'le formulaire en ligne' : 'Excel' }} sont listées ci-dessous avec tous leurs détails.
        </p>
      </div>
      
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 text-xs">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-2 py-2 text-center text-xs font-medium text-black uppercase">
                <input 
                  type="checkbox" 
                  :checked="isAllSelected"
                  :indeterminate="isIndeterminate"
                  @change="toggleSelectAll"
                  class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                  title="Sélectionner tout"
                />
              </th>
              <th class="px-2 py-2 text-left text-xs font-medium text-black uppercase">Référence</th>
              <th class="px-2 py-2 text-left text-xs font-medium text-black uppercase">Marque</th>
              <th class="px-2 py-2 text-left text-xs font-medium text-black uppercase">Categorie</th>
              <th class="px-2 py-2 text-left text-xs font-medium text-black uppercase">Vendu Par</th>
              <th class="px-2 py-2 text-left text-xs font-medium text-black uppercase">Collection</th>
              <th class="px-2 py-2 text-left text-xs font-medium text-black uppercase">Pays Origine</th>
              <th class="px-2 py-2 text-left text-xs font-medium text-black uppercase">Taille Paquet</th>
              <th class="px-2 py-2 text-left text-xs font-medium text-black uppercase">Qté Paquet</th>
              <th class="px-2 py-2 text-left text-xs font-medium text-black uppercase">Stock</th>
              <th class="px-2 py-2 text-left text-xs font-medium text-black uppercase">Prix</th>
              <th class="px-2 py-2 text-left text-xs font-medium text-black uppercase">Prix Réd.</th>
              <th class="px-2 py-2 text-left text-xs font-medium text-black uppercase">Poids</th>
              <th class="px-2 py-2 text-left text-xs font-medium text-black uppercase">Couleur</th>
              <th class="px-2 py-2 text-left text-xs font-medium text-black uppercase">Min. Cmd</th>
              <th class="px-2 py-2 text-left text-xs font-medium text-black uppercase">Dimensions</th>
              <th class="px-2 py-2 text-left text-xs font-medium text-black uppercase">Caract.</th>
              <th class="px-2 py-2 text-left text-xs font-medium text-black uppercase">Date Création</th>
              <th class="px-2 py-2 text-left text-xs font-medium text-black uppercase sticky right-0 bg-gray-50 z-10">Action</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <template v-for="reference in references" :key="reference.id">
              <!-- Ligne principale -->
              <tr 
                @click="toggleDetails(reference.id)"
                :class="[
                  'cursor-pointer transition-colors',
                  selectedId === reference.id ? 'bg-blue-50' : 'hover:bg-gray-50',
                  selectedIds.has(reference.id) ? 'bg-green-50' : ''
                ]"
              >
                <td class="px-2 py-2 text-center" @click.stop>
                  <input 
                    type="checkbox" 
                    :checked="selectedIds.has(reference.id)"
                    @change="toggleSelect(reference.id)"
                    class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                  />
                </td>
                <td class="px-2 py-2 whitespace-nowrap text-xs text-black font-medium">{{ reference.reference || '-' }}</td>
                <td class="px-2 py-2 text-xs text-black">{{ getDisplayValue('marque', reference.marque) }}</td>
                <td class="px-2 py-2 text-xs text-black">{{ getCategorieFullPath(reference) }}</td>
                <td class="px-2 py-2 text-xs text-black">{{ reference.venduPar || '-' }}</td>
                <td class="px-2 py-2 text-xs text-black">{{ getDisplayValue('collection', reference.collection) }}</td>
                <td class="px-2 py-2 text-xs text-black">{{ getDisplayValue('pays', reference.paysOrigine) }}</td>
                <td class="px-2 py-2 text-xs text-black">{{ getDisplayValue('taillePaquet', reference.taillePaquet) }}</td>
                <td class="px-2 py-2 text-xs text-black">{{ reference.venduPar === 'tailles' ? '-' : getDisplayValue('quantitePaquet', reference.quantitePaquet) }}</td>
                <td class="px-2 py-2 text-xs text-black">
                  <template v-if="reference.venduPar === 'couleurs' && reference.couleurs && reference.couleurs.some((c: any) => c.stock != null)">
                    <span v-for="(c, idx) in reference.couleurs.filter((c: any) => c.stock != null)" :key="c.id">
                      {{ c.nom }}*{{ (c as any).stock }}<span v-if="idx < reference.couleurs.filter((c: any) => c.stock != null).length - 1">, </span>
                    </span>
                  </template>
                  <template v-else-if="reference.venduPar === 'tailles' && reference.couleurs && reference.couleurs.some((c: any) => Array.isArray((c as any).tailleStocks) && (c as any).tailleStocks.some((ts: any) => ts.quantity != null))">
                    <span v-for="(c, cidx) in reference.couleurs.filter((c: any) => Array.isArray((c as any).tailleStocks))" :key="c.id">
                      <span v-for="(ts, tidx) in (c as any).tailleStocks.filter((ts: any) => ts.quantity != null)" :key="ts.taille">{{ c.nom }}*{{ ts.taille }}*{{ ts.quantity }}<span v-if="tidx < (c as any).tailleStocks.filter((ts: any) => ts.quantity != null).length - 1">,</span></span><span v-if="cidx < reference.couleurs.filter((c: any) => Array.isArray((c as any).tailleStocks)).length - 1">, </span>
                    </span>
                  </template>
                  <template v-else>{{ reference.stock || '-' }}</template>
                </td>
                <td class="px-2 py-2 whitespace-nowrap text-xs text-black">{{ reference.prix ? `${reference.prix}€` : '-' }}</td>
                <td class="px-2 py-2 whitespace-nowrap text-xs text-black">{{ reference.prixReduit ? `${reference.prixReduit}€` : '-' }}</td>
                <td class="px-2 py-2 text-xs text-black">{{ reference.poids || '-' }}</td>
                <td class="px-2 py-2 text-xs text-black">
                  <span v-if="reference.couleurs && reference.couleurs.length > 0">
                    {{ reference.couleurs.length }} couleur{{ reference.couleurs.length > 1 ? 's' : '' }}
                  </span>
                  <span v-else>-</span>
                </td>
                <td class="px-2 py-2 text-xs text-black">{{ reference.minimumCommande || '-' }}</td>
                <td class="px-2 py-2 text-xs text-black">{{ reference.dimensions || '-' }}</td>
                <td class="px-2 py-2 text-xs text-black">
                  <span v-if="reference.caracteristiques && reference.caracteristiques.length > 0" class="text-blue-600">
                    {{ reference.caracteristiques.length }} caract.
                  </span>
                  <span v-else class="text-black">-</span>
                </td>
                <td class="px-2 py-2 text-xs text-black">{{ reference.dateCreation }}</td>
                <td class="px-2 py-2 whitespace-nowrap text-sm font-medium sticky right-0 bg-white z-10" @click.stop>
                  <div class="flex items-center gap-2">
                    <button
                      @click.stop="$emit('edit', reference)"
                      class="px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                      title="Modifier cette référence"
                    >
                      <i class="fas fa-pen text-xs"></i>
                    </button>
                    <button
                      @click.stop="$emit('duplicate', reference)"
                      class="px-2 py-1 bg-amber-600 text-white rounded hover:bg-amber-700"
                      title="Dupliquer"
                    >
                      <i class="fas fa-layer-group text-xs"></i>
                    </button>
                    <button
                      @click.stop="$emit('remove', reference.id)"
                      class="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                      title="Supprimer cette référence"
                    >
                      <i class="fas fa-trash text-xs"></i>
                    </button>
                  </div>
                </td>
              </tr>

              <!-- Ligne expandable pour les détails -->
              <tr v-if="selectedId === reference.id" class="bg-gray-50">
                <td colspan="19" class="p-0">
                  <div class="bg-white border border-gray-200 rounded mx-1 my-1 p-3">
                    <div class="flex items-center justify-between mb-2">
                      <h5 class="text-xs font-semibold text-black">
                        Détails de la référence : {{ reference.reference }}
                      </h5>
                      <button 
                        @click="selectedId = null"
                        class="text-black hover:text-black text-xs"
                      >
                        <i class="fas fa-times"></i>
                      </button>
                    </div>

                    <!-- Détails des descriptions -->
                    <div v-if="reference.descriptionFr || reference.descriptionEn || reference.descriptionIt || reference.descriptionEs || reference.descriptionDe" class="mb-3">
                      <h6 class="text-xs font-medium text-black mb-1">Descriptions :</h6>
                      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                        <div v-if="reference.descriptionFr" class="text-xs bg-gray-50 p-2 rounded border">
                          <span class="font-medium text-black">FR :</span>
                          <div class="text-black mt-1">{{ reference.descriptionFr }}</div>
                        </div>
                        <div v-if="reference.descriptionEn" class="text-xs bg-gray-50 p-2 rounded border">
                          <span class="font-medium text-black">EN :</span>
                          <div class="text-black mt-1">{{ reference.descriptionEn }}</div>
                        </div>
                        <div v-if="reference.descriptionIt" class="text-xs bg-gray-50 p-2 rounded border">
                          <span class="font-medium text-black">IT :</span>
                          <div class="text-black mt-1">{{ reference.descriptionIt }}</div>
                        </div>
                        <div v-if="reference.descriptionEs" class="text-xs bg-gray-50 p-2 rounded border">
                          <span class="font-medium text-black">ES :</span>
                          <div class="text-black mt-1">{{ reference.descriptionEs }}</div>
                        </div>
                        <div v-if="reference.descriptionDe" class="text-xs bg-gray-50 p-2 rounded border">
                          <span class="font-medium text-black">DE :</span>
                          <div class="text-black mt-1">{{ reference.descriptionDe }}</div>
                        </div>
                      </div>
                    </div>

                    <!-- Détails des compositions -->
                    <div v-if="reference.compositions && reference.compositions.length > 0" class="mb-3">
                      <h6 class="text-xs font-medium text-black mb-1">Compositions :</h6>
                      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        <div 
                          v-for="(comp, idx) in reference.compositions" 
                          :key="idx"
                          class="text-xs bg-gray-50 p-2 rounded border"
                        >
                          <div class="flex items-center justify-between gap-2 text-xs">
                            <span class="font-medium text-black truncate">
                              {{ getCompositionLabel(comp) }}
                            </span>
                            <span
                              v-if="getCompositionLocalisationLabel(comp)"
                              class="text-black whitespace-nowrap"
                            >
                              {{ getCompositionLocalisationLabel(comp) }}
                            </span>
                            <span class="text-black whitespace-nowrap">
                              {{ getCompositionPourcentage(comp) }}&nbsp;%
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Détails des couleurs -->
                    <div v-if="reference.couleurs && reference.couleurs.length > 0" class="mb-3">
                      <h6 class="text-xs font-medium text-black mb-1">Couleurs :</h6>
                      <div class="flex flex-wrap gap-1">
                        <span 
                          v-for="couleur in reference.couleurs" 
                          :key="couleur.id"
                          :class="[
                            'inline-flex items-center px-2 py-1 rounded-full text-xs border',
                            couleur.isMain 
                              ? 'bg-blue-500 text-white border-blue-600 font-medium' 
                              : 'bg-gray-100 text-black border-gray-300'
                          ]"
                        >
                          {{ couleur.nom }}
                          <span v-if="couleur.isMain" class="ml-1 text-xs">★</span>
                        </span>
                      </div>
                    </div>

                    <!-- Détails des caractéristiques -->
                    <div v-if="reference.caracteristiques && reference.caracteristiques.length > 0" class="mb-3">
                      <h6 class="text-xs font-medium text-black mb-1">Caractéristiques sélectionnées :</h6>
                      <div class="flex flex-wrap gap-1">
                        <span 
                          v-for="carac in reference.caracteristiques" 
                          :key="carac.id"
                          class="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 border border-blue-200"
                        >
                          <i class="fas fa-check-circle mr-1 text-xs"></i>
                          {{ carac.libelle }}
                        </span>
                      </div>
                    </div>

                    <!-- Informations complémentaires -->
                    <div class="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      <div v-if="reference.dateRemise">
                        <span class="font-medium text-black">Date remise :</span>
                        <div class="text-black">{{ formatDateFr(reference.dateRemise) }}</div>
                      </div>
                      <div v-if="reference.pourcentageRemise">
                        <span class="font-medium text-black">% Remise :</span>
                        <div class="text-black">{{ reference.pourcentageRemise }}%</div>
                      </div>
                      <div v-if="reference.stock">
                        <span class="font-medium text-black">Stock :</span>
                        <div class="text-black">{{ reference.stock }}</div>
                      </div>
                      <div v-if="reference.poids">
                        <span class="font-medium text-black">Poids :</span>
                        <div class="text-black">{{ reference.poids }}</div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      
      <!-- Bouton de confirmation -->
      <div v-if="references.length > 0" class="mt-4">
        <!-- Mobile: centré et vertical -->
        <div class="lg:hidden">
          <div class="text-sm text-black text-center mb-4">
            <span v-if="selectedIds.size > 0">
              {{ selectedIds.size }} référence{{ selectedIds.size > 1 ? 's' : '' }} sélectionnée{{ selectedIds.size > 1 ? 's' : '' }}
            </span>
            <span v-else class="text-black italic">
              Aucune sélection (toutes les références seront mises en ligne)
            </span>
          </div>
          <div class="flex flex-col items-center gap-2 w-full max-w-xs mx-auto">
            <ActionButton
              v-if="shootingOnly || showPrimaryAction"
              :label="shootingOnly ? 'Faire une demande' : actionLabel('Faire une demande')"
              :variant="showPrimaryAction && !shootingOnly ? 'success' : 'primary'"
              icon="fas fa-camera"
              customClass="w-full"
              :loading="loading"
              :disabled="loading"
              @click="handleConfirm('shooting')"
            />
            <ActionButton
              v-if="showPrimaryAction && !shootingOnly"
              :label="actionLabel(primaryActionLabel)"
              variant="primary"
              icon="fas fa-images"
              customClass="w-full"
              :loading="loading"
              :disabled="loading"
              @click="handleConfirm('primary')"
            />
            <ActionButton
              v-else-if="!shootingOnly"
              :label="actionLabel('Choisir le shooting')"
              variant="secondary"
              icon="fas fa-camera"
              customClass="w-full"
              :disabled="loading"
              @click="handleConfirm('shooting')"
            />
            <ActionButton
              label="Vider les brouillons"
              icon="fas fa-trash"
              variant="danger"
              customClass="w-full"
              @click="showClearConfirm = true"
            />
          </div>
        </div>
        <!-- Desktop: texte à gauche, boutons à droite -->
        <div class="hidden lg:flex lg:items-center lg:justify-between">
          <div class="text-sm text-black">
            <span v-if="selectedIds.size > 0">
              {{ selectedIds.size }} référence{{ selectedIds.size > 1 ? 's' : '' }} sélectionnée{{ selectedIds.size > 1 ? 's' : '' }}
            </span>
            <span v-else class="text-black italic">
              Aucune sélection (toutes les références seront mises en ligne)
            </span>
          </div>
          <div class="flex items-center gap-2">
            <ActionButton
              label="Vider les brouillons"
              icon="fas fa-trash"
              variant="danger"
              @click="showClearConfirm = true"
            />
            <ActionButton
              v-if="shootingOnly || showPrimaryAction"
              :label="shootingOnly ? 'Faire une demande' : actionLabel('Faire une demande')"
              :variant="showPrimaryAction && !shootingOnly ? 'success' : 'primary'"
              icon="fas fa-camera"
              :loading="loading"
              :disabled="loading"
              @click="handleConfirm('shooting')"
            />
            <ActionButton
              v-if="showPrimaryAction && !shootingOnly"
              :label="actionLabel(primaryActionLabel)"
              variant="primary"
              icon="fas fa-images"
              :loading="loading"
              :disabled="loading"
              @click="handleConfirm('primary')"
            />
            <ActionButton
              v-else-if="!shootingOnly"
              :label="actionLabel('Choisir le shooting')"
              variant="secondary"
              icon="fas fa-camera"
              :disabled="loading"
              @click="handleConfirm('shooting')"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de confirmation pour vider les brouillons -->
    <ModalComp
      :isVisible="showClearConfirm"
      title="Confirmer la suppression"
      size="sm"
      @update:isVisible="showClearConfirm = $event"
    >
      <template #body>
        <p class="text-black">
          Êtes-vous sûr de vouloir supprimer tous les brouillons ? Cette action est irréversible.
        </p>
      </template>
      <template #footer>
        <div class="flex justify-end gap-2">
          <ActionButton
            label="Annuler"
            variant="secondary"
            @click="showClearConfirm = false"
          />
          <ActionButton
            label="Supprimer tout"
            variant="danger"
            icon="fas fa-trash"
            @click="handleClearAll"
          />
        </div>
      </template>
    </ModalComp>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { ActionButton, ModalComp } from '@/components'
import type { MelReference } from '@/types/mel'
import type { ReferenceLookupData } from '@/types/mel-components'
import { formatDeclinaisonLabel } from '@/utils/declinaisonLabel'

interface Props {
  references: MelReference[]
  source: 'form' | 'excel'
  lookupData: ReferenceLookupData
  loading?: boolean
  primaryActionLabel?: string
  showPrimaryAction?: boolean
  shootingOnly?: boolean
}

interface Emits {
  (e: 'remove', id: string): void
  (e: 'confirm', selectedIds: string[], action: 'primary' | 'shooting'): void
  (e: 'duplicate', reference: MelReference): void
  (e: 'clearAll'): void
  (e: 'edit', reference: MelReference): void
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  primaryActionLabel: 'Ajouter mes photos',
  showPrimaryAction: true,
  shootingOnly: false,
})
const emit = defineEmits<Emits>()

const selectedId = ref<string | null>(null)
const selectedIds = ref<Set<string>>(new Set())

// Nettoyer selectedIds quand des références sont supprimées
watch(
  () => props.references,
  (newRefs) => {
    const validIds = new Set(newRefs.map(r => r.id))
    const currentSelected = selectedIds.value
    let hasStale = false
    for (const id of currentSelected) {
      if (!validIds.has(id)) {
        hasStale = true
        break
      }
    }
    if (hasStale) {
      selectedIds.value = new Set(
        Array.from(currentSelected).filter(id => validIds.has(id))
      )
    }
  },
  { deep: false }
)

const expandedColors = ref<Set<string>>(new Set())
const showClearConfirm = ref(false)

const handleClearAll = () => {
  showClearConfirm.value = false
  emit('clearAll')
}

const isAllSelected = computed(() => {
  return props.references.length > 0 && selectedIds.value.size === props.references.length
})

const isIndeterminate = computed(() => {
  return selectedIds.value.size > 0 && selectedIds.value.size < props.references.length
})

const toggleSelect = (id: string) => {
  const newSet = new Set(selectedIds.value)
  if (newSet.has(id)) {
    newSet.delete(id)
  } else {
    newSet.add(id)
  }
  selectedIds.value = newSet
}

const toggleSelectAll = () => {
  if (isAllSelected.value) {
    selectedIds.value = new Set()
  } else {
    selectedIds.value = new Set(props.references.map(r => r.id))
  }
}

const actionLabel = (label: string) => {
  const count = selectedIds.value.size || props.references.length
  return `${label} (${count})`
}

const handleConfirm = (action: 'primary' | 'shooting') => {
  // If no selection, emit all ids; otherwise emit selected ids
  const ids = selectedIds.value.size > 0 
    ? Array.from(selectedIds.value)
    : props.references.map(r => r.id)
  emit('confirm', ids, action)
}

const toggleDetails = (id: string) => {
  selectedId.value = selectedId.value === id ? null : id
}

const toggleColorExpand = (id: string) => {
  const newSet = new Set(expandedColors.value)
  if (newSet.has(id)) {
    newSet.delete(id)
  } else {
    newSet.add(id)
  }
  expandedColors.value = newSet
}

const getMainColor = (reference: MelReference) => {
  if (!reference.couleurs || reference.couleurs.length === 0) return null
  // Trouver la couleur principale (isMain=true) ou prendre la première
  const main = reference.couleurs.find(c => c.isMain)
  return main || reference.couleurs[0]
}

const getOtherColors = (reference: MelReference) => {
  if (!reference.couleurs || reference.couleurs.length <= 1) return []
  const mainColor = getMainColor(reference)
  return reference.couleurs.filter(c => c.id !== mainColor?.id)
}

const getCategorieFullPath = (reference: MelReference): string => {
  const parts: string[] = []
  if (reference.categorieLibelle) parts.push(reference.categorieLibelle)
  if (reference.sousCategorieLibelle) parts.push(reference.sousCategorieLibelle)
  if (reference.sousSousCategorieLibelle) parts.push(reference.sousSousCategorieLibelle)
  if (parts.length > 0) return parts.join(' > ')
  // Fallback: lookup by id
  return getDisplayValue('categorie', reference.categorie)
}

const resolveCompositionMateriauLabel = (id: number): string | null => {
  const list = props.lookupData.compositionMateriaux
  if (!list?.length || !Number.isFinite(id) || id <= 0) return null
  const hit = list.find((m) => Number(m.id) === id)
  return hit ? hit.label : null
}

const getCompositionLabel = (comp: NonNullable<MelReference['compositions']>[number]): string => {
  const lookup = props.lookupData.compositionMateriaux
  const idFromField = Number(comp.compositionId ?? comp.id_composition)
  if (Number.isFinite(idFromField) && idFromField > 0) {
    const fromLookup = resolveCompositionMateriauLabel(idFromField)
    if (fromLookup) return fromLookup
  }

  const candidates = [
    comp.materiau,
    comp.compositionName,
    comp.libelle,
    comp.name,
  ]
    .map((s) => String(s ?? '').trim())
    .filter(Boolean)

  for (const t of candidates) {
    if (!/^\d+$/.test(t)) return t
  }

  for (const t of candidates) {
    if (/^\d+$/.test(t) && lookup?.length) {
      const n = parseInt(t, 10)
      const fromLookup = resolveCompositionMateriauLabel(n)
      if (fromLookup) return fromLookup
    }
  }

  if (candidates.length) return candidates[0]
  if (Number.isFinite(idFromField) && idFromField > 0) {
    return `Composition n°${idFromField}`
  }
  return '—'
}

const getCompositionPourcentage = (comp: NonNullable<MelReference['compositions']>[number]): string => {
  const p = comp.pourcentage ?? comp.percentage
  if (p === '' || p == null) return '0'
  const n =
    typeof p === 'string'
      ? parseFloat(String(p).replace(',', '.').replace('%', '').trim())
      : Number(p)
  if (!Number.isFinite(n)) return '0'
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100)
}

const resolveCompositionLocalisationLabel = (id: number): string | null => {
  const list = props.lookupData.compositionLocalisations
  if (!list?.length || !Number.isFinite(id) || id <= 0) return null
  const hit = list.find((l) => Number(l.id) === id)
  return hit ? hit.label : null
}

const getCompositionLocalisationLabel = (comp: NonNullable<MelReference['compositions']>[number]): string => {
  // Priorité à l'id (source de vérité) pour éviter un libellé par défaut erroné type « Produit ».
  const locIdNum = Number(comp.localisationId)
  if (Number.isFinite(locIdNum) && locIdNum > 0) {
    const resolved = resolveCompositionLocalisationLabel(locIdNum)
    if (resolved) return `Zone : ${resolved}`
  }

  const loc = comp.localisation
  if (loc != null && loc !== '' && loc !== '0') {
    const locStr = String(loc).trim()
    if (/^\d+$/.test(locStr)) {
      const n = parseInt(locStr, 10)
      if (n > 0) {
        const resolved = resolveCompositionLocalisationLabel(n)
        if (resolved) return `Zone : ${resolved}`
        return `Zone (id) : ${locStr}`
      }
    }
  }

  const byName = comp.localisationName?.trim()
  if (byName) return `Zone : ${byName}`

  if (loc == null || loc === '' || loc === '0') return ''
  const locStrFree = String(loc).trim()
  return locStrFree ? `Zone : ${locStrFree}` : ''
}

const getDisplayValue = (type: string, value: any, libelle?: string): string => {
  if (!value && !libelle) return '-'
  
  switch (type) {
    case 'marque': {
      // Priorité au libellé fourni directement (depuis la base de données) - compatible avec l'ancien PHP
      if (libelle) return libelle
      if (!value) return '-'
      // Compatibilité: value peut être string ou number, marques.id est number
      const valueNum = typeof value === 'string' ? parseInt(value, 10) : value
      const marque = props.lookupData.marques.find(m => m.id === valueNum || m.value === value?.toString())
      return marque ? marque.label : value
    }
    
    case 'categorie': {
      if (libelle) return libelle
      const categorie = props.lookupData.categories.find(c => c.id === parseInt(value))
      return categorie ? categorie.label : value
    }
    
    case 'collection': {
      const collection = props.lookupData.collections.find(c => c.id.toString() === value.toString())
      return collection ? collection.label : value
    }
    
    case 'pays': {
      const pays = props.lookupData.provenances.find(p => p.id.toString() === value.toString())
      return pays ? pays.label : value
    }
    
    case 'taillePaquet': {
      if (!props.lookupData.declinaisons) return value
      const declinaison = props.lookupData.declinaisons.find(d => d.id.toString() === value.toString())
      return declinaison
        ? formatDeclinaisonLabel(declinaison.label, declinaison.tailles)
        : value
    }
    
    case 'quantitePaquet': {
      if (!props.lookupData.packs) return value
      const pack = props.lookupData.packs.find(p => p.id.toString() === value.toString())
      return pack ? (pack.value ? `${pack.label} (${pack.value})` : pack.label) : value
    }
    
    default:
      return value
  }
}

const formatDateFr = (rawDate: string | null | undefined): string => {
  const value = String(rawDate ?? '').trim()
  if (!value) return '-'

  // Déjà en jj/mm/aaaa
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return value

  // Formats ISO/mysql: yyyy-mm-dd ou yyyy-mm-dd HH:mm:ss
  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (isoMatch) {
    const [, y, m, d] = isoMatch
    return `${d}/${m}/${y}`
  }

  // Fallback: tenter un parse JS
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  const d = String(parsed.getDate()).padStart(2, '0')
  const m = String(parsed.getMonth() + 1).padStart(2, '0')
  const y = parsed.getFullYear()
  return `${d}/${m}/${y}`
}
</script>
